import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEO, { SITE_URL } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";

/**
 * Renderer commun des landing pages SEO FR (cocon sémantique).
 * `content` : { path, title, description, h1, heroParas, heroImage:{src,alt}, sections, faq, cta }
 * sections: [{ h2, paras:[jsx], items:[string], subs:[{h3, text:jsx}] }]
 * Les grappes de mots-clés Semrush sont documentées dans chaque page (commentaire, non rendu).
 */
export default function SeoLanding({ content }) {
  const c = content;
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "WebPage",
      name: c.title, description: c.description, url: `${SITE_URL}${c.path}`,
      publisher: { "@type": "Organization", name: "Kalypso Music", url: SITE_URL, logo: `${SITE_URL}/logo.png` },
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: c.h1, item: `${SITE_URL}${c.path}` },
      ],
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: c.faq.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.aText || f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO title={c.title} description={c.description} path={c.path} jsonLd={jsonLd} ogImage={`${SITE_URL}${c.heroImage.src}`} />
      <Navbar />
      <main>
        <section className="border-b border-border/60 bg-card/30 py-14 sm:py-20">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-8">
            <div>
              <h1 className="font-heading text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">{c.h1}</h1>
              {c.heroParas.map((p, i) => (
                <p key={i} className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{p}</p>
              ))}
              <div className="mt-7">
                <Link to="/create">
                  <Button size="lg" className="rounded-full" data-testid={`seo-hero-cta${c.path}`}>
                    {c.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <img src={c.heroImage.src} alt={c.heroImage.alt} loading="eager"
              className="mx-auto w-full max-w-xs rounded-2xl border border-border shadow-xl lg:max-w-sm" />
          </div>
        </section>

        <section className="mx-auto max-w-3xl space-y-14 px-4 py-16 sm:px-6 lg:px-8">
          {c.sections.map((s, i) => (
            <article key={i}>
              <h2 className="font-heading text-xl font-semibold sm:text-2xl">{s.h2}</h2>
              {(s.paras || []).map((p, j) => (
                <p key={j} className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">{p}</p>
              ))}
              {s.items && (
                <ul className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                  {s.items.map((it, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /><span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(s.subs || []).map((sub, k) => (
                <div key={k} className="mt-5">
                  <h3 className="font-heading text-base font-semibold">{sub.h3}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{sub.text}</p>
                </div>
              ))}
            </article>
          ))}

          <article>
            <h2 className="font-heading text-xl font-semibold sm:text-2xl">{c.faqTitle}</h2>
            <div className="mt-5 space-y-4">
              {c.faq.map((f, i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4">
                  <h3 className="font-heading text-sm font-semibold">{f.q}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </article>

          <div className="rounded-3xl border border-primary/25 bg-primary/5 p-8 text-center">
            <h2 className="font-heading text-xl font-semibold">{c.finalTitle}</h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">{c.finalText}</p>
            <Link to="/create" className="mt-5 inline-block">
              <Button size="lg" className="rounded-full" data-testid={`seo-final-cta${c.path}`}>{c.cta}</Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
